import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMusicPlayer } from '../../MusicPlayerContext';
import { useUser } from '../../UserContext';
import { AdminContext } from '../../AdminContext';
import { getFreeMemberNewSongInfo } from '../../utils/playbackGate';
import { showUiHint, showHintPlus } from '../../utils/uiHint';
import { getCoverImageUrl, hasVideo } from '../../utils/musicStreamUrls';
import { isInstrumentalMusic, normalizeInstrumentalTitle } from '../../utils/musicMeta';
import { spaNavigate } from '../../utils/spaNavigate';
import HomeGlassCard from './HomeGlassCard';
import { buttonClass, theme } from '../../ui/theme';
import { limitPlayAll } from '../../utils/playAllLimit';

function formatDuration(seconds) {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function getTitleByLang(music, lang) {
  if (!music) return '';
  const title = (() => {
    switch (lang) {
      case 'cn':
        return music.title_zh_cn || music.title || '';
      case 'tw':
        return music.title_zh_tw || music.title || '';
      case 'ja':
        return music.title_ja || music.title || '';
      case 'en':
        return music.title_en || music.title || '';
      default:
        return music.title || '';
    }
  })();
  return normalizeInstrumentalTitle(music, title);
}

/**
 * 首页轻量排行榜预览
 * - 默认：近一周播放榜 top10
 */
export default function HomeRankingsPreview({
  limit = 6,
  type = 'play',
  period = 'week',
  currentLang = 'ja',
  onOpenAll = null
}) {
  const { i18n, t } = useTranslation();
  const { user } = useUser();
  const { isAdmin } = React.useContext(AdminContext);
  const { playMusicNow, primePlayMusic, setCurrentMusic, addToPlaylist, setPlaying } = useMusicPlayer();

  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const lang = i18n?.language || currentLang || 'ja';
  const btnOutlineSm = buttonClass({ variant: 'outline', size: 'sm' });

  const fetchRankings = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/music/rankings', {
        params: { type, period, limit }
      });
      if (res?.data?.success) {
        setItems(Array.isArray(res.data.data?.rankings) ? res.data.data.rankings : []);
      } else {
        setError(res?.data?.error || t('musicRankings.fetchFailed', '获取排行榜失败'));
      }
    } catch (e) {
      setError(e?.response?.data?.error || t('musicRankings.fetchFailed', '获取排行榜失败'));
    } finally {
      setLoading(false);
    }
  }, [type, period, limit, t]);

  React.useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  const handlePlay = React.useCallback(
    (music, e) => {
      try {
        e?.stopPropagation?.();
      } catch (_) {}
      if (!music) return;
      const gate = getFreeMemberNewSongInfo({ music, user, isAdmin: !!isAdmin });
      if (gate?.blocked) {
        showUiHint(t('musicList.freeMemberNewSongHint', { days: Number(gate?.daysLeft || 0) }), {
          duration: 2200
        });
        return;
      }
      playMusicNow(music);
    },
    [user, isAdmin, t, playMusicNow]
  );

  const handleOpenDetail = React.useCallback(
    (music, e) => {
      try {
        e?.stopPropagation?.();
      } catch (_) {}
      if (!music?.id) return;
      spaNavigate(`/${lang}/music/${music.id}.html`);
    },
    [lang]
  );

  const handlePlayAll = React.useCallback(
    (e) => {
      try {
        e?.stopPropagation?.();
      } catch (_) {}
      if (!items || items.length === 0) return;
      if (!user && !isAdmin) {
        showHintPlus(t('musicList.guestContinuousPlayHint'));
        return;
      }
      const playable = [];
      let firstBlocked = null;
      items.forEach((music) => {
        const gate = getFreeMemberNewSongInfo({ music, user, isAdmin: !!isAdmin });
        if (gate?.blocked) {
          if (!firstBlocked) firstBlocked = gate;
          return;
        }
        playable.push(music);
      });
      if (playable.length === 0) {
        if (firstBlocked) {
          showUiHint(t('musicList.freeMemberNewSongHint', { days: Number(firstBlocked?.daysLeft || 0) }), {
            duration: 2200
          });
        }
        return;
      }
      const limited = limitPlayAll(playable);
      limited.forEach((music) => addToPlaylist(music));
      setCurrentMusic(limited[0]);
      setPlaying(true);
      primePlayMusic(limited[0]);
    },
    [items, user, isAdmin, t, addToPlaylist, setCurrentMusic, setPlaying, primePlayMusic]
  );

  return (
    <HomeGlassCard>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-white text-lg font-semibold">
            {t('homeLanding.rankingsPreviewTitle', '今日热听')}
          </div>
          <div className="text-xs text-white/60 mt-1 whitespace-nowrap">
            {t('homeLanding.rankingsPreviewDesc', '看看现在大家都在听什么。')}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            className={[btnOutlineSm, 'whitespace-nowrap'].join(' ')}
            onClick={handlePlayAll}
            disabled={loading || !!error || items.length === 0}
          >
            {t('homeLanding.rankingsPreviewPlayAll', '播放全部')}
          </button>
          <button
            type="button"
            className={[theme.links.subtleUnderline, 'whitespace-nowrap'].join(' ')}
            onClick={() => (onOpenAll ? onOpenAll() : null)}
          >
            {t('homeLanding.rankingsPreviewMore', '查看完整排行榜')}
          </button>
        </div>
      </div>

      <div className="mt-3 divide-y divide-white/10">
        {loading ? (
          <div className="py-4 text-sm text-white/70">{t('musicRankings.loading', '加载中...')}</div>
        ) : error ? (
          <div className="py-4 text-sm text-red-200 flex items-center justify-between gap-3">
            <div className="truncate">{error}</div>
            <button
              type="button"
              className={btnOutlineSm}
              onClick={fetchRankings}
            >
              {t('musicRankings.retry', '重试')}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="py-4 text-sm text-white/70">{t('musicRankings.noData', '暂无数据')}</div>
        ) : (
          items.map((music, idx) => {
            const title = getTitleByLang(music, lang);
            const cover = getCoverImageUrl(music) || (hasVideo(music) ? '/icons/mtv.png' : '');
            return (
              <div
                key={music.id || idx}
                className={['py-3 cursor-pointer group', theme.surfaces.glassRow].join(' ')}
                onClick={(e) => handleOpenDetail(music, e)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 text-center text-sm font-semibold text-white/70">{idx + 1}</div>
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/10 flex items-center justify-center flex-shrink-0">
                    {cover ? (
                      <img src={cover} alt={title} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <span className="text-xs text-white/50">No</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white font-medium truncate inline-flex items-center gap-1">
                      {hasVideo(music) && (
                        <img
                          src="/icons/mtv-s.png"
                          alt="MTV"
                          className="w-4 h-4 flex-shrink-0 object-contain"
                          title={t('player.hasVideo') || '视频'}
                        />
                      )}
                      {!hasVideo(music) && isInstrumentalMusic(music) && (
                        <img
                          src="/icons/mcd-s.png"
                          alt={t('musicList.categoryInstrumental', '纯音乐')}
                          className="w-4 h-4 flex-shrink-0 object-contain"
                          title={t('musicList.categoryInstrumental', '纯音乐')}
                        />
                      )}
                      <span className="truncate">{title || t('musicList.tableTitle', '标题')}</span>
                    </div>
                    <div className="text-xs text-white/60 flex items-center gap-2">
                      <span className="truncate">{music.album || ''}</span>
                      <span className="opacity-40">·</span>
                      <span className="tabular-nums">{formatDuration(music.duration)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      className={buttonClass({
                        variant: 'outline',
                        size: 'sm',
                        className: 'bg-blue-500/20 border-blue-400/25 hover:bg-blue-500/25'
                      })}
                      onClick={(e) => handlePlay(music, e)}
                    >
                      {t('musicList.play', '播放')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </HomeGlassCard>
  );
}

